import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ShoppingCart, ChevronRight, Home as HomeIcon, Users, Mountain, Package, Star, Shield, Check, Heart, Sprout, AlertTriangle, Briefcase, Send, Mail, Leaf, UsersRound, Percent } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useCart } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { calculateSalePrice } from '../utils/calculateSalePrice';
import { getRegionPrice } from '../utils/getRegionPrice';
import { useCurrency } from '../contexts/CurrencyContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Comprehensive Lifestyle Segments Data (Inspired by Harvest Right)
const lifestyleData = {
  families: {
    title: 'Freeze Drying for Families',
    headline: 'Healthy Snacks & Meals Your Family Will Love',
    subtitle: 'Create preservative-free snacks, complete meals, and even treats for your pets. Save money while giving your family the best.',
    heroImage: 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=1920',
    icon: HomeIcon,
    color: '#CE8946',
    stats: [
      { value: '97%', label: 'Nutrition Retained' },
      { value: '25+', label: 'Years Shelf Life' },
      { value: '$2,000+', label: 'Annual Savings' }
    ],
    benefits: [
      { title: 'Healthy Snacks Kids Love', desc: 'Freeze-dried fruits and yogurt drops are crunchy, delicious, and 100% natural.' },
      { title: 'Save Money on Groceries', desc: 'Buy in bulk during sales and preserve at peak freshness. Never waste food again.' },
      { title: 'Emergency Food Supply', desc: 'Build a family emergency food reserve that actually tastes good.' },
      { title: 'Treats for Pets', desc: 'Make healthy, single-ingredient treats your pets will go crazy for.' }
    ],
    useCases: [
      { title: 'Fruit Snacks', desc: 'Strawberries, bananas, and more', image: 'https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=400' },
      { title: 'Yogurt Drops', desc: 'Probiotic treats kids love', image: 'https://images.unsplash.com/photo-1488477181946-6428a0291777?w=400' },
      { title: 'Complete Meals', desc: 'Soups, casseroles, pasta dishes', image: 'https://images.unsplash.com/photo-1547592180-85f173990554?w=400' },
      { title: 'Pet Treats', desc: 'Chicken, liver, fish treats', image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'medium'
  },
  growers: {
    title: 'Freeze Drying for Growers',
    headline: 'Preserve Your Harvest at Peak Freshness',
    subtitle: 'For anyone with a garden, farm, orchard, or livestock. Freeze drying is the ideal way to preserve your hard work.',
    heroImage: 'https://images.unsplash.com/photo-1592838064575-70ed626d3a0e?w=1920',
    icon: Sprout,
    color: '#4CAF50',
    stats: [
      { value: '100%', label: 'Of Harvest Preserved' },
      { value: '90%', label: 'Less Storage Space' },
      { value: '365', label: 'Days of Fresh Taste' }
    ],
    benefits: [
      { title: 'Preserve Entire Harvests', desc: 'Process tomatoes, peppers, corn, and more at their peak ripeness.' },
      { title: 'Year-Round Availability', desc: 'Enjoy your garden produce in the middle of winter.' },
      { title: 'Preserve Eggs & Dairy', desc: 'Scrambled eggs and cheese that last for decades.' },
      { title: 'Herbs & Seasonings', desc: 'Build your own spice cabinet from your garden.' }
    ],
    useCases: [
      { title: 'Garden Vegetables', desc: 'Tomatoes, peppers, corn, beans', image: 'https://images.unsplash.com/photo-1518977676601-b53f82ber?w=400' },
      { title: 'Fresh Eggs', desc: 'Scrambled eggs that last years', image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=400' },
      { title: 'Orchard Fruits', desc: 'Apples, peaches, berries', image: 'https://images.unsplash.com/photo-1619566636858-adf3ef46400b?w=400' },
      { title: 'Fresh Herbs', desc: 'Basil, oregano, mint, cilantro', image: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'large'
  },
  adventurers: {
    title: 'Freeze Drying for Adventurers',
    headline: 'Lightweight Meals for the Great Outdoors',
    subtitle: 'Perfect for hikers, campers, hunters, and anyone who loves the outdoors. Create nutritious meals that weigh ounces, not pounds.',
    heroImage: 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=1920',
    icon: Mountain,
    color: '#2196F3',
    stats: [
      { value: '90%', label: 'Weight Reduction' },
      { value: '5 min', label: 'Rehydration Time' },
      { value: '50%', label: 'Cost Savings vs Store' }
    ],
    benefits: [
      { title: 'Ultralight Meals', desc: 'Full dinners that weigh just a few ounces in your pack.' },
      { title: 'Real Home Cooking', desc: 'Enjoy your favorite meals on the trail, not bland store-bought.' },
      { title: 'Custom Nutrition', desc: 'Tailor meals to your dietary needs and caloric requirements.' },
      { title: 'Emergency Backup', desc: 'Always have food for unexpected situations.' }
    ],
    useCases: [
      { title: 'Trail Dinners', desc: 'Pasta, rice, curry dishes', image: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=400' },
      { title: 'Breakfast Scrambles', desc: 'Hot breakfast in 5 minutes', image: 'https://images.unsplash.com/photo-1525351484163-7529414344d8?w=400' },
      { title: 'Energy Snacks', desc: 'Fruit, jerky, trail mix', image: 'https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?w=400' },
      { title: 'Hunting Provisions', desc: 'Multi-day trip food supply', image: 'https://images.unsplash.com/photo-1551524559-8af4e6624178?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'small'
  },
  'emergency-prep': {
    title: 'Freeze Drying for Emergency Preparedness',
    headline: 'Be Ready for Whatever Comes',
    subtitle: 'Build an emergency food supply that lasts 25+ years and actually tastes like real food. Peace of mind for your family.',
    heroImage: 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=1920',
    icon: AlertTriangle,
    color: '#FF5722',
    stats: [
      { value: '25+', label: 'Years Shelf Life' },
      { value: '2,000+', label: 'Calories per Batch' },
      { value: '100%', label: 'Self-Reliant' }
    ],
    benefits: [
      { title: 'Longest Shelf Life', desc: '25+ years when properly stored - the best of any preservation method.' },
      { title: 'Real Nutrition', desc: 'Retains 97% of nutritional value vs canned or dehydrated.' },
      { title: 'Complete Meals', desc: 'Store full meals, not just ingredients that need cooking.' },
      { title: 'Rotation Friendly', desc: 'Use oldest first and easily replenish your supply.' }
    ],
    useCases: [
      { title: 'Complete Meals', desc: 'Just add water for hot food', image: 'https://images.unsplash.com/photo-1547592180-85f173990554?w=400' },
      { title: 'Protein Storage', desc: 'Meat, eggs, dairy preserved', image: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=400' },
      { title: 'Fruits & Veggies', desc: 'Essential vitamins stored', image: 'https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=400' },
      { title: 'Special Diets', desc: 'Medical & dietary needs', image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'large'
  },
  'pet-lovers': {
    title: 'Freeze Drying for Pet Lovers',
    headline: 'Premium Treats Your Pets Deserve',
    subtitle: 'Create single-ingredient, healthy treats for dogs, cats, and other pets. Know exactly what your furry friends are eating.',
    heroImage: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1920',
    icon: Heart,
    color: '#E91E63',
    stats: [
      { value: '100%', label: 'Natural Ingredients' },
      { value: '0', label: 'Preservatives' },
      { value: '80%', label: 'Cost Savings' }
    ],
    benefits: [
      { title: 'Single Ingredient Treats', desc: 'Pure chicken, beef, fish, or liver - nothing else added.' },
      { title: 'Control Quality', desc: 'Use the same quality meat you\'d eat yourself.' },
      { title: 'Save Money', desc: 'Premium pet treats cost $30+/lb in stores. Make them for a fraction.' },
      { title: 'Special Diets', desc: 'Perfect for pets with allergies or special dietary needs.' }
    ],
    useCases: [
      { title: 'Chicken Treats', desc: 'Pure freeze-dried chicken', image: 'https://images.unsplash.com/photo-1604503468506-a8da13d82791?w=400' },
      { title: 'Liver Snacks', desc: 'Dogs go crazy for liver', image: 'https://images.unsplash.com/photo-1601758124510-52d02ddb7cbd?w=400' },
      { title: 'Fish Treats', desc: 'Great for cats and dogs', image: 'https://images.unsplash.com/photo-1534766555764-ce878a5e3a2b?w=400' },
      { title: 'Raw Food Diet', desc: 'Preserve raw pet food safely', image: 'https://images.unsplash.com/photo-1568640347023-a616a30bc3bd?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'small'
  },
  'healthy-living': {
    title: 'Freeze Drying for Healthy Living',
    headline: 'Nutrition Without Compromise',
    subtitle: 'Preserve the vitamins, minerals, and enzymes in your food. Perfect for health-conscious individuals and families.',
    heroImage: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=1920',
    icon: Heart,
    color: '#9C27B0',
    stats: [
      { value: '97%', label: 'Nutrients Preserved' },
      { value: '0', label: 'Additives Needed' },
      { value: '100%', label: 'Whole Foods' }
    ],
    benefits: [
      { title: 'Maximum Nutrition', desc: 'Freeze drying preserves more nutrients than any other method.' },
      { title: 'No Preservatives', desc: 'Just pure, whole food - nothing added, nothing removed.' },
      { title: 'Smoothie Ready', desc: 'Add freeze-dried fruits and veggies to smoothies instantly.' },
      { title: 'Portion Control', desc: 'Pre-portion healthy meals for easy calorie management.' }
    ],
    useCases: [
      { title: 'Smoothie Packs', desc: 'Pre-portioned fruit blends', image: 'https://images.unsplash.com/photo-1505252585461-04db1eb84625?w=400' },
      { title: 'Superfood Powders', desc: 'Kale, spinach, spirulina', image: 'https://images.unsplash.com/photo-1622597467836-f3285f2131b8?w=400' },
      { title: 'Healthy Snacks', desc: 'Fruit chips, veggie crisps', image: 'https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=400' },
      { title: 'Meal Prep', desc: 'Healthy meals ready to go', image: 'https://images.unsplash.com/photo-1547592180-85f173990554?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'medium'
  },
  homesteaders: {
    title: 'Freeze Drying for Homesteaders',
    headline: 'True Food Independence',
    subtitle: 'For those committed to self-sufficient living. Preserve everything from your land and achieve real food security.',
    heroImage: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1920',
    icon: Users,
    color: '#795548',
    stats: [
      { value: '100%', label: 'Self-Sufficient' },
      { value: '12', label: 'Months of Food' },
      { value: '$5,000+', label: 'Annual Value' }
    ],
    benefits: [
      { title: 'Process Everything', desc: 'Meat, dairy, eggs, produce - preserve it all from your homestead.' },
      { title: 'Year-Round Security', desc: 'Build a complete food supply from what you grow and raise.' },
      { title: 'Reduce Waste', desc: 'Never lose produce to spoilage or freezer burn again.' },
      { title: 'Trade & Sell', desc: 'Create valuable products to trade or sell at markets.' }
    ],
    useCases: [
      { title: 'Meat Preservation', desc: 'Beef, pork, chicken, game', image: 'https://images.unsplash.com/photo-1607623814075-e51df1bdc82f?w=400' },
      { title: 'Dairy Products', desc: 'Milk, cheese, butter, cream', image: 'https://images.unsplash.com/photo-1628088062854-d1870b4553da?w=400' },
      { title: 'Garden Harvest', desc: 'All your vegetables & fruits', image: 'https://images.unsplash.com/photo-1592838064575-70ed626d3a0e?w=400' },
      { title: 'Eggs', desc: 'Scrambled eggs that last years', image: 'https://images.unsplash.com/photo-1582722872445-44dc5f7e3c8f?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'xl'
  },
  entrepreneurs: {
    title: 'Freeze Drying for Entrepreneurs',
    headline: 'Start Your Freeze-Dried Food Business',
    subtitle: 'Turn your passion into profit. Create unique products for farmers markets, online stores, or wholesale accounts.',
    heroImage: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1920',
    icon: Briefcase,
    color: '#607D8B',
    stats: [
      { value: '300%+', label: 'Profit Margins' },
      { value: '$50K+', label: 'Revenue Potential' },
      { value: '1000s', label: 'Of Products Possible' }
    ],
    benefits: [
      { title: 'High Margins', desc: 'Freeze-dried products command premium prices with great margins.' },
      { title: 'Low Startup Cost', desc: 'Start from home with minimal investment compared to other food businesses.' },
      { title: 'Viral Products', desc: 'Freeze-dried candy and snacks are trending and highly shareable.' },
      { title: 'Scale Gradually', desc: 'Start small and grow as demand increases.' }
    ],
    useCases: [
      { title: 'Freeze-Dried Candy', desc: 'Viral sensation products', image: 'https://images.unsplash.com/photo-1582058091505-f87a2e55a40f?w=400' },
      { title: 'Pet Treat Business', desc: 'Premium pet treats', image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400' },
      { title: 'Specialty Foods', desc: 'Gourmet ingredients', image: 'https://images.unsplash.com/photo-1606787366850-de6330128bfc?w=400' },
      { title: 'Gift Products', desc: 'Curated food gift boxes', image: 'https://images.unsplash.com/photo-1549465220-1a8b9238cd48?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'large'
  },
  'dietary-restrictions': {
    title: 'Freeze Drying for Dietary Restrictions',
    headline: 'Control Your Ingredients, Control Your Health',
    subtitle: 'For those managing allergies, celiac disease, or special diets. Know exactly what\'s in your food with complete ingredient control.',
    heroImage: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=1920',
    icon: Leaf,
    color: '#8BC34A',
    stats: [
      { value: '100%', label: 'Ingredient Control' },
      { value: '0', label: 'Hidden Allergens' },
      { value: '25+', label: 'Years Shelf Life' }
    ],
    benefits: [
      { title: 'Allergen-Free Meals', desc: 'Create meals free from gluten, dairy, nuts, or any allergen with complete control.' },
      { title: 'Know Every Ingredient', desc: 'No hidden additives, preservatives, or cross-contamination risks.' },
      { title: 'Convenient & Safe', desc: 'Always have safe meals ready - at home, work, school, or traveling.' },
      { title: 'Save Money', desc: 'Specialty allergen-free foods are expensive. Make your own for a fraction of the cost.' }
    ],
    useCases: [
      { title: 'Gluten-Free Meals', desc: 'Safe pasta, breads, snacks', image: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400' },
      { title: 'Dairy-Free Options', desc: 'Coconut yogurt, nut milks', image: 'https://images.unsplash.com/photo-1550583724-b2692b85b150?w=400' },
      { title: 'Nut-Free Snacks', desc: 'Safe school snacks for kids', image: 'https://images.unsplash.com/photo-1464965911861-746a04b4bca6?w=400' },
      { title: 'Keto & Paleo Meals', desc: 'Low-carb freeze-dried foods', image: 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'medium'
  },
  'community-groups': {
    title: 'Freeze Drying for Community Groups',
    headline: 'Serve Your Community with Quality Food',
    subtitle: 'Perfect for churches, food banks, scout troops, and community organizations looking to provide nutritious meals and build food security.',
    heroImage: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=1920',
    icon: UsersRound,
    color: '#9C27B0',
    stats: [
      { value: '1000s', label: 'Meals Preserved' },
      { value: '25+', label: 'Years Shelf Life' },
      { value: '50%', label: 'Cost Reduction' }
    ],
    benefits: [
      { title: 'Food Bank Donations', desc: 'Donate shelf-stable, nutritious food that lasts for decades.' },
      { title: 'Church Food Programs', desc: 'Build emergency food supplies for members in need.' },
      { title: 'Scout & Youth Programs', desc: 'Teach food preservation and create lightweight camping meals.' },
      { title: 'Community Resilience', desc: 'Prepare your community for emergencies with long-term food storage.' }
    ],
    useCases: [
      { title: 'Food Bank Meals', desc: 'Nutritious donations that last', image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=400' },
      { title: 'Church Potlucks', desc: 'Preserve community recipes', image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400' },
      { title: 'Youth Camp Food', desc: 'Lightweight, tasty trail meals', image: 'https://images.unsplash.com/photo-1478131143081-80f7f84ca84d?w=400' },
      { title: 'Emergency Supplies', desc: 'Community disaster readiness', image: 'https://images.unsplash.com/photo-1584568694244-14fbdf83bd30?w=400' }
    ],
    testimonial: null,  // Use real testimonials from admin
    recommendedSize: 'large'
  }
};

// Request Info Form Component
const RequestInfoForm = ({ segment, lifestyle }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    segment: segment
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/leads`, {
        ...formData,
        source: `Lifestyle Landing Page - ${lifestyle.title}`,
        interests: [segment]
      });
      toast.success("Thanks! We'll be in touch soon.");
      setFormData({ name: '', email: '', phone: '', message: '', segment });
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <Input
          placeholder="Your Name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          required
          className="bg-white"
        />
        <Input
          type="email"
          placeholder="Email Address"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          className="bg-white"
        />
      </div>
      <Input
        type="tel"
        placeholder="Phone Number (Optional)"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        className="bg-white"
      />
      <Textarea
        placeholder="Tell us about your freeze drying goals..."
        value={formData.message}
        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
        rows={3}
        className="bg-white"
      />
      <Button type="submit" disabled={loading} className="w-full bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold py-6">
        {loading ? 'Sending...' : 'Get Personalized Recommendations'}
        <Send className="ml-2 h-4 w-4" />
      </Button>
    </form>
  );
};

// Newsletter Signup Component
const NewsletterSignup = ({ segment }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email,
        interests: [segment, 'tips', 'deals']
      });
      setSubscribed(true);
      toast.success("You're subscribed!");
    } catch (error) {
      if (error.response?.data?.detail === 'Email already subscribed') {
        toast.success("You're already on the list!");
        setSubscribed(true);
      } else {
        toast.error('Something went wrong.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <div className="flex items-center gap-3 bg-[#003456]/10 p-4 rounded-xl">
        <Check className="h-6 w-6 text-[#003456]" />
        <span className="text-[#003456] font-medium">You're subscribed! Check your inbox.</span>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="flex gap-3">
      <div className="flex-1 relative">
        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email for tips & deals"
          required
          className="pl-10 bg-white"
        />
      </div>
      <Button type="submit" disabled={loading} className="bg-[#003456] hover:bg-[#002B47] text-white px-6">
        {loading ? '...' : 'Subscribe'}
      </Button>
    </form>
  );
};

const LifestylePage = () => {
  const { slug } = useParams();
  const [products, setProducts] = useState([]);
  const lifestyle = lifestyleData[slug];
  const { currency, formatPrice, formatMoney } = useCurrency();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/products`);
        // Filter to recommended size or show first 3
        setProducts(response.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    
    // Track page view for lifestyle page (for analytics)
    const trackPageView = async () => {
      try {
        const visitorId = localStorage.getItem('visitor_id') || null;
        const trackRes = await axios.post(`${API_URL}/api/landing-pages/lifestyle-${slug}/track-view`, null, {
          params: { visitor_id: visitorId }
        });
        if (trackRes.data?.visitor_id && !localStorage.getItem('visitor_id')) {
          localStorage.setItem('visitor_id', trackRes.data.visitor_id);
        }
      } catch (error) {
        // Silent fail - analytics shouldn't break the page
      }
    };
    
    // Track for segment suggestions
    const trackForSegments = async () => {
      try {
        // Get or create session ID
        let sessionId = sessionStorage.getItem('browsing_session_id');
        if (!sessionId) {
          sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
          sessionStorage.setItem('browsing_session_id', sessionId);
        }
        
        // Get email if subscribed
        let email = null;
        try {
          const subscriber = localStorage.getItem('newsletter_subscriber');
          if (subscriber) email = JSON.parse(subscriber).email;
        } catch {}
        
        await axios.post(`${API_URL}/api/segments/suggestions/track`, {
          session_id: sessionId,
          page_url: `/lifestyle/${slug}`,
          page_title: lifestyle?.title || `Freeze Drying for ${slug}`,
          email: email
        });
      } catch (error) {
        // Silent fail
      }
    };
    
    fetchProducts();
    trackPageView();
    trackForSegments();
    // Scroll to top on page load
    window.scrollTo(0, 0);
  }, [slug, lifestyle]);

  if (!lifestyle) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Page Not Found</h1>
          <Link to="/preview" className="text-[#003456] hover:underline">Return Home</Link>
        </div>
      </div>
    );
  }

  const IconComponent = lifestyle.icon;

  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img src={lifestyle.heroImage} alt={lifestyle.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0203]/95 via-[#003456]/80 to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-md mb-6" style={{ backgroundColor: `${lifestyle.color}20`, border: `1px solid ${lifestyle.color}` }}>
                <IconComponent className="h-5 w-5" style={{ color: lifestyle.color }} />
                <span style={{ color: lifestyle.color }} className="font-medium">{lifestyle.title}</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">{lifestyle.headline}</h1>
              <p className="text-xl text-white/80 mb-8">{lifestyle.subtitle}</p>
              
              {/* Stats */}
              <div className="flex gap-8 mb-8">
                {lifestyle.stats.map((stat, i) => (
                  <div key={i} className="text-center">
                    <div className="text-3xl font-bold" style={{ color: lifestyle.color }}>{stat.value}</div>
                    <div className="text-white/60 text-sm">{stat.label}</div>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/products">
                  <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold px-8 py-6 text-lg">
                    Shop Freeze Dryers <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="#request-info">
                  <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg">
                    Get Free Info
                  </Button>
                </a>
              </div>
            </div>
            
            {/* Quick Newsletter Signup */}
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
              <h3 className="text-xl font-bold text-white mb-2">Get Expert Tips & Exclusive Deals</h3>
              <p className="text-white/70 mb-6">Join thousands of {slug.replace('-', ' ')} who love freeze drying.</p>
              <NewsletterSignup segment={slug} />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Why Freeze Drying is Perfect for You</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Discover how a D-Freeze freeze dryer can transform your {slug.replace('-', ' ')} lifestyle.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lifestyle.benefits.map((benefit, i) => (
              <div key={i} className="bg-[#F8FAFC] rounded-2xl p-6 hover:shadow-lg transition-shadow">
                <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: `${lifestyle.color}20` }}>
                  <Check className="h-6 w-6" style={{ color: lifestyle.color }} />
                </div>
                <h3 className="text-lg font-bold text-[#0a0203] mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">What You Can Freeze Dry</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Popular freeze drying projects for {slug.replace('-', ' ')}.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {lifestyle.useCases.map((useCase, i) => (
              <Link 
                key={i} 
                to={`/recipes?category=${slug}`}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group"
              >
                <div className="aspect-video relative overflow-hidden">
                  <img src={useCase.image} alt={useCase.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-[#0a0203] mb-1 group-hover:text-[#003456] transition-colors">{useCase.title}</h3>
                  <p className="text-gray-600 text-sm">{useCase.desc}</p>
                  <span className="text-[#003456] text-sm font-medium mt-2 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    View Recipes <ChevronRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to={`/recipes?category=${slug}`}>
              <Button variant="outline" className="border-[#003456] text-[#003456] hover:bg-[#003456] hover:text-white">
                Browse All {lifestyle.title} Recipes
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonial - only render if testimonial is not null */}
      {lifestyle.testimonial && (
        <section className="py-16 bg-[#003456]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <div className="text-6xl text-[#CE8946] mb-6">"</div>
            <p className="text-2xl text-white mb-6 italic">{lifestyle.testimonial.quote}</p>
            <p className="text-white/60">- {lifestyle.testimonial.author}</p>
          </div>
        </section>
      )}

      {/* Recommended Products */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0a0203] mb-4">Recommended For You</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Based on your needs as a {slug.replace('-', ' ')} enthusiast, we recommend these models.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {products.map((product) => (
              <Link key={product._id || product.id} to={`/products/${product._id || product.id}`} className="group bg-[#F8FAFC] rounded-2xl overflow-hidden hover:shadow-xl transition-shadow">
                <div className="aspect-square relative overflow-hidden">
                  <img 
                    src={product.image_url || (product.images && product.images[0]) || '/placeholder-product.jpg'} 
                    alt={product.name} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                    onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=400'; }}
                  />
                  {product.size === lifestyle.recommendedSize && (
                    <div className="absolute top-4 left-4 bg-[#CE8946] text-[#0a0203] px-3 py-1 rounded text-sm font-bold">
                      Best for You
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-[#0a0203] mb-2">{product.name}</h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between">
                    {(() => {
                      const priceInfo = calculateSalePrice(product, currency);
                      if (priceInfo.isOnSale && priceInfo.effectivePrice) {
                        return (
                          <div className="flex items-center gap-2">
                            <p className="text-2xl font-bold text-[#003456]">{formatMoney(priceInfo.effectivePrice)}</p>
                            <span className="text-sm text-gray-400 line-through">{formatMoney(priceInfo.originalPrice)}</span>
                            <span className="bg-red-500 text-white px-2 py-0.5 rounded text-xs font-bold">{priceInfo.savingsPercent}% OFF</span>
                          </div>
                        );
                      } else {
                        return <p className="text-2xl font-bold text-[#003456]">{formatMoney(getRegionPrice(product, currency))}</p>;
                      }
                    })()}
                    <Button size="sm" className="bg-[#003456] hover:bg-[#002B47] text-white">
                      View Details
                    </Button>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/products">
              <Button variant="outline" className="border-[#003456] text-[#003456] px-8 py-6">
                View All Products <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Request Info Form Section */}
      <section id="request-info" className="py-20 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full mx-auto flex items-center justify-center mb-4" style={{ backgroundColor: `${lifestyle.color}20` }}>
                <IconComponent className="h-8 w-8" style={{ color: lifestyle.color }} />
              </div>
              <h2 className="text-3xl font-bold text-[#0a0203] mb-2">Get Personalized Recommendations</h2>
              <p className="text-gray-600">Tell us about your freeze drying goals and we'll help you find the perfect solution.</p>
            </div>
            <RequestInfoForm segment={slug} lifestyle={lifestyle} />
            <p className="text-center text-gray-500 text-sm mt-6">
              We respect your privacy. No spam, ever.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-[#003456]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Start Freeze Drying?</h2>
          <p className="text-white/80 mb-8 text-lg">Join thousands of satisfied customers who have transformed their {slug.replace('-', ' ')} lifestyle with D-Freeze.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/products">
              <Button className="bg-[#CE8946] hover:bg-[#00a9b1] text-[#0a0203] font-semibold px-8 py-6 text-lg">
                Shop Now <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/compare">
              <Button variant="outline" className="border-white text-white hover:bg-white hover:text-[#003456] px-8 py-6 text-lg">
                Compare Models
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LifestylePage;
